import { createSlice } from "@reduxjs/toolkit";
import undoable, { excludeAction } from "redux-undo";
import {
  BaseSrcIntDestPlateInfo,
  LabwareTypeInfo,
  WorklistValues,
} from "../shared/interfaces";
import {
  getIntermediatePlateInfoState,
  getSourcePlateInfoState,
} from "./state-helpers";

//ExternalThawToolState
export interface ExternalThawToolState {
  sourcePlateInfo: SourcePlateInfoState[];
  intPlateInfo: any[];
  destPlateInfo: DestPlateInfoState[];
  worklistValues: WorklistValuesState;
  deadPlateBarcode: string;
  deadPlateType: string;
}

export interface SourcePlateInfoState extends BaseSrcIntDestPlateInfo {
  operatingVol: number;
  wellInfo: any[];
}

export interface IntermediatePlateInfoState extends BaseSrcIntDestPlateInfo {
  operatingVol: number;
  resuspensionVol: number;
  intWellVol: number;
}

export interface DestPlateInfoState extends BaseSrcIntDestPlateInfo {
  index: number;
  operatingVol: number;
  minOperatingVol: number;
  preprocess: boolean;
  topup: boolean;
  topUpVol: number;
  startsInIncubator: boolean;
  endsInIncubator: boolean;
}

export interface WorklistWellMappingState {
  sourcePlateIndex: number;
  sourceWellId: string;
  destPlateIndex: number;
  destWellId: string;
  transferVol?: string;
}

type DestPlateKeys = keyof DestPlateInfoState;
export type DestPlateStringKeys = Extract<DestPlateKeys, string>;

export interface WorklistValuesState {
  harvestWells: WorklistWellMappingState[];
  int1ToDest: WorklistWellMappingState[];
}

export interface ExtThawDestPlateLabwareTypeInfo extends LabwareTypeInfo {
  topup?: boolean;
  startsInIncubator?: boolean;
  endsInIncubator?: boolean;
  preprocess?: boolean;
}

export const externalThawInitialToolState: ExternalThawToolState = {
  sourcePlateInfo: Array.from({ length: 2 }, () => getSourcePlateInfoState()),
  intPlateInfo: Array.from({ length: 1 }, () =>
    getIntermediatePlateInfoState()
  ),
  destPlateInfo: [
    {
      index: 0,
      plateBarcode: "",
      labwareTypeCode: "",
      operatingVol: 0,
      minOperatingVol: 0,
      rows: [],
      cols: [],
      preprocess: true,
      topup: true,
      topUpVol: 0,
      startsInIncubator: true,
      endsInIncubator: true,
    },
  ],
  deadPlateBarcode: "",
  deadPlateType: "",
  worklistValues: {
    harvestWells: [],
    int1ToDest: [],
  },
};

export const ExternalThawToolStoreSlice = createSlice({
  name: "external-thaw-tool-info",
  initialState: externalThawInitialToolState,
  reducers: {
    RESET_STATE: (state) => {
      state.sourcePlateInfo = externalThawInitialToolState.sourcePlateInfo;
      state.destPlateInfo = externalThawInitialToolState.destPlateInfo;
      state.intPlateInfo = externalThawInitialToolState.intPlateInfo;
      state.worklistValues = externalThawInitialToolState.worklistValues;
      state.deadPlateBarcode = externalThawInitialToolState.deadPlateBarcode;
    },
    UPLOAD_SOURCE_PLATE: (state, action) => {
      state.sourcePlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.sourcePlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.sourcePlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.sourcePlateInfo[action.payload.index].rows = action.payload.rows;
      state.sourcePlateInfo[action.payload.index].cols = action.payload.cols;
      state.sourcePlateInfo[action.payload.index].wellInfo =
        action.payload.wellInfo;
    },
    ADD_DESTINATION_PLATE: (state, action) => {
      const lastIndex =
        state.destPlateInfo.length > 0
          ? state.destPlateInfo[state.destPlateInfo.length - 1].index
          : -1;
      state.destPlateInfo.push({
        index: lastIndex + 1,
        plateBarcode: "",
        labwareTypeCode: "",
        operatingVol: 0,
        minOperatingVol: 0,
        rows: [],
        cols: [],
        preprocess: true,
        topup: true,
        topUpVol: 0,
        startsInIncubator: true,
        endsInIncubator: true,
      });
    },
    SET_DESTINATION_PLATE: (state, action) => {
      state.destPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.destPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.destPlateInfo[action.payload.index].minOperatingVol =
        action.payload.minOperatingVol;
      state.destPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.destPlateInfo[action.payload.index].rows = action.payload.rows;
      state.destPlateInfo[action.payload.index].cols = action.payload.cols;
      state.destPlateInfo[action.payload.index].topup = action.payload.topup;
      state.destPlateInfo[action.payload.index].topUpVol =
        action.payload.topUpVol;
      state.destPlateInfo[action.payload.index].startsInIncubator =
        action.payload.startsInIncubator;
      state.destPlateInfo[action.payload.index].endsInIncubator =
        action.payload.endsInIncubator;
      state.destPlateInfo[action.payload.index].preprocess =
        action.payload.preprocess;
    },
    CLEAR_SOURCE_PLATE: (state, action) => {
      state.sourcePlateInfo[action.payload.index].plateBarcode = "";
      state.sourcePlateInfo[action.payload.index].labwareTypeCode = "";
      state.sourcePlateInfo[action.payload.index].operatingVol = 0;
      state.sourcePlateInfo[action.payload.index].rows = [];
      state.sourcePlateInfo[action.payload.index].cols = [];
      state.sourcePlateInfo[action.payload.index].wellInfo = [];
      state.worklistValues.harvestWells =
        state.worklistValues.harvestWells.filter(
          (e) => e.sourcePlateIndex !== action.payload.index
        );
    },
    CLEAR_INTERMEDIATE_PLATE: (state, action) => {
      state.intPlateInfo[action.payload.index].plateBarcode = "";
      state.intPlateInfo[action.payload.index].labwareTypeCode = "";
      state.intPlateInfo[action.payload.index].operatingVol = 0;
      state.intPlateInfo[action.payload.index].resuspensionVol = 0;
      state.intPlateInfo[action.payload.index].rows = [];
      state.intPlateInfo[action.payload.index].cols = [];
    },
    CLEAR_DESTINATION_PLATE: (state, action) => {
      state.destPlateInfo[action.payload.index].plateBarcode = "";
      state.destPlateInfo[action.payload.index].operatingVol = 0;
      state.destPlateInfo[action.payload.index].labwareTypeCode = "";
      state.destPlateInfo[action.payload.index].rows = [];
      state.destPlateInfo[action.payload.index].cols = [];
      state.destPlateInfo[action.payload.index].preprocess = true;
      state.destPlateInfo[action.payload.index].topup = true;
      state.destPlateInfo[action.payload.index].topUpVol = 0;
      state.destPlateInfo[action.payload.index].startsInIncubator = true;
      state.destPlateInfo[action.payload.index].endsInIncubator = true;
      state.worklistValues.int1ToDest = state.worklistValues.int1ToDest.filter(
        (e) => e.destPlateIndex !== action.payload.index
      );
      if (state.destPlateInfo.length > 1) {
        const removedIndex = action.payload.index;

        state.destPlateInfo.splice(removedIndex, 1);

        state.worklistValues.int1ToDest = state.worklistValues.int1ToDest.map(
          (destWell) => {
            if (destWell.destPlateIndex > removedIndex) {
              destWell.destPlateIndex -= 1;
            }
            return destWell;
          }
        );

        state.destPlateInfo.map((destplate) => {
          if (destplate.index > removedIndex) {
            destplate.index -= 1;
          }
          return destplate;
        });
      }
    },
    CLONE_DESTINATION_PLATE: (state, action) => {
      const destinationIndex = action.payload.destinationIndex;

      const newInt1ToDest = state.worklistValues.int1ToDest.filter(
        (e) => e.destPlateIndex === destinationIndex
      );

      state.worklistValues.int1ToDest.push(
        ...newInt1ToDest.map((item) => {
          return { ...item, destPlateIndex: destinationIndex + 1 };
        })
      );
    },
    SET_INTERMEDIATE_PLATE: (state, action) => {
      state.intPlateInfo[action.payload.index].operatingVol = Math.floor(
        action.payload.operatingVol * 0.9
      ); //Reduced by 10% to account for bubbling
      state.intPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.intPlateInfo[action.payload.index].rows = action.payload.rows;
      state.intPlateInfo[action.payload.index].cols = action.payload.cols;
    },
    UPDATE_AUX_PLATEBARCODES: (state, action) => {
      if (state.intPlateInfo[0].labwareTypeCode !== "") {
        state.intPlateInfo[0].plateBarcode = action.payload.intPlateBarcode;
      }
      state.deadPlateBarcode = action.payload.deadPlateBarcode;
    },
    UPDATE_INT_PLATE_INFO: (state, action) => {
      state.intPlateInfo[action.payload.index][action.payload.key] =
        action.payload.value;
    },
    UPDATE_DEAD_PLATEBARCODE: (state, action) => {
      state.deadPlateBarcode = action.payload.value;
    },
    UPDATE_DEAD_PLATETYPE: (state, action) => {
      state.deadPlateType = action.payload.value;
    },

    INT_TO_DEST_ADDED: (state, action) => {
      state.worklistValues.int1ToDest.push(...action.payload.selections);
    },

    SRC_TO_INT_MANUALLY_ADDED: (state, action) => {
      state.worklistValues.harvestWells.push(...action.payload.selections);
    },

    CLEAR_INT_WELLS: (state) => {
      state.worklistValues.harvestWells = [];
    },
    UPDATE_INTERMEDIATE_PLATE_INFO: (state, action) => {
      state.intPlateInfo[action.payload.index][action.payload.key] =
        action.payload.value;
    },

    //Updates dest plate info
    UPDATE_DESTINATION_PLATE_INFO: (state, action) => {
      const index = action.payload.index;
      const key = action.payload.key as DestPlateStringKeys;
      const value = action.payload.value;
      if (typeof state.destPlateInfo[index][key] == typeof value) {
        //@ts-ignore
        state.destPlateInfo[index][key] = value;
      }
    },
    UPLOAD_SOURCE_INT_DEAD_AND_DEST_PLATE_INFO: (state, action) => {
      state.sourcePlateInfo = action.payload.sourcePlateInfo;
      state.intPlateInfo = action.payload.intPlateInfo;
      state.destPlateInfo = action.payload.destPlateInfo;
      state.deadPlateBarcode = action.payload.deadPlateBarcode;
      state.deadPlateType = action.payload.deadPlateType;
    },
    UPLOAD_WORKLIST_VALUES: (state, action) => {
      const properties: string[] = ["harvestWells", "int1ToDest"];
      properties.forEach((property) => {
        const value = Reflect.get(action.payload, property);
        Reflect.set(state.worklistValues, property, value);
      });
    },

    UPDATE_ALL_DEST_STAMP_CELL_NUMBER: (state, action) => {
      const int1ToDest = state.worklistValues.int1ToDest.map((e) => ({
        ...e,
        transferVol: action.payload.transferVol,
      }));

      state.worklistValues.int1ToDest = int1ToDest;
    },
    UPDATE_EMPTY_DEST_CELL_NUMBERS: (state, action) => {
      state.worklistValues.int1ToDest = state.worklistValues.int1ToDest.map(
        (wellMapping) =>
          wellMapping.transferVol === ""
            ? { ...wellMapping, transferVol: action.payload.transferVol }
            : wellMapping
      );
    },
    UPDATE_SPECIFIC_DEST_CELL_NUMBERS: (state, action) => {
      state.worklistValues.int1ToDest = state.worklistValues.int1ToDest.map(
        (wellMapping) =>
          wellMapping.destPlateIndex === action.payload.destPlateIndex
            ? { ...wellMapping, transferVol: action.payload.transferVol }
            : wellMapping
      );
    },
    UPDATE_DEST_CELL_NUMBER: (state, action) => {
      const int1ToDest: WorklistValues[] = [];

      for (const item of action.payload.rows) {
        int1ToDest.push(item);
      }

      state.worklistValues.int1ToDest = int1ToDest;
    },
    UPLOAD_PLATES_FROM_TEMPLATE: (state, action) => {
      for (let i = 0; i < 8; i++) {
        if (i >= action.payload.sourcePlates.length) break;
        state.sourcePlateInfo[i] = action.payload.sourcePlates[i];
      }

      for (let i = 0; i < 3; i++) {
        if (i >= action.payload.intPlates.length) break;
        state.intPlateInfo[i] = action.payload.intPlates[i];
      }

      state.destPlateInfo = action.payload.destPlates;
      state.deadPlateType = action.payload.deadPlateType;
    },
    UPLOAD_WORKLIST_VALUES_FROM_TEMPLATE: (state, action) => {
      state.worklistValues.harvestWells = action.payload.harvestWells;
      state.worklistValues.int1ToDest = action.payload.int1ToDest;
    },
  },
});

export const ExternalThawToolReducer = ExternalThawToolStoreSlice.reducer;

export const ExternalThawToolActions = ExternalThawToolStoreSlice.actions;

export const ExternalThawTool = undoable(ExternalThawToolReducer, {
  filter: excludeAction([
    ExternalThawToolActions.UPDATE_DESTINATION_PLATE_INFO.toString(),
    ExternalThawToolActions.UPLOAD_WORKLIST_VALUES_FROM_TEMPLATE.toString(),
  ]),
});
